/**
 * ConfirmActionModal Component
 *
 * Thin wrapper around {@link ConfirmationModal} that automatically
 * resolves the cancel label via the UI toolkit translation provider
 * (key: `modules.confirmActionModal.cancel`).
 *
 * Unlike the base ConfirmationModal this component does NOT accept an
 * `open` prop — visibility is controlled by conditionally rendering it
 * (i.e. `{show && <ConfirmActionModal … />}`).
 *
 * @example
 * ```tsx
 * {showConfirm && (
 *   <ConfirmActionModal
 *     title="Delete user"
 *     message="Are you sure?"
 *     confirmLabel="Delete"
 *     variant="danger"
 *     onConfirm={handleDelete}
 *     onClose={() => setShowConfirm(false)}
 *   />
 * )}
 * ```
 */
import * as React from 'react';
import { type TestIdProps } from '../../utils';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { useTranslation } from '../../providers/UIProvider';

export interface ConfirmActionModalProps extends TestIdProps {
  /** Modal title */
  title: string;
  /** Descriptive message — can be a string or rich ReactNode */
  message: React.ReactNode;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Called when the user confirms the action */
  onConfirm: () => void;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Whether the confirm action is in progress */
  loading?: boolean;
  /** Semantic variant controlling the confirm button style */
  variant?: 'danger' | 'warning' | 'primary';
  /** Optional warning message displayed between header and footer */
  warning?: React.ReactNode;
  /** Additional CSS classes for the modal wrapper */
  className?: string;
}

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
  loading = false,
  variant = 'danger',
  warning,
  className,
  testId,
  id
}) => {
  const t = useTranslation('modules.confirmActionModal');

  return (
    <ConfirmationModal
      open={true}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={t('cancel')}
      variant={variant}
      loading={loading}
      warning={warning}
      onConfirm={onConfirm}
      onClose={onClose}
      className={className}
      testId={testId}
      id={id}
    />
  );
};

ConfirmActionModal.displayName = 'ConfirmActionModal';
